// "Müşteri Ziyaret Sırası Düzenle" modalı — seçili gün grubundaki müşterileri
// HTML5 sürükle-bırak ile yeniden sıralar; sağda numaralı rota önizlemesi gösterir.
// Backend: PUT /api/route-plans/{id}/customers/reorder.
import { useState } from 'react';
import { s, authHeaders } from '../listPageStyles';
import {
  API_BASE, pick, dayLabel, dayColor, frequencyLabel,
  hasGeo, optimizeByNearestNeighbor, routeLengthKm,
} from './routePlanUtils';
import RouteMap from './RouteMap';

export default function VisitOrderModal({ planId, day, customers, onClose, onSaved, showToast, readOnly = false }) {
  const [list, setList] = useState(() => [...customers]);
  const [dragIdx, setDragIdx] = useState(null);
  const [busy, setBusy] = useState(false);

  const color = dayColor(day);

  const onDrop = (toIdx) => {
    if (dragIdx === null || dragIdx === toIdx) return;
    setList((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
    setDragIdx(null);
  };

  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    setList((prev) => {
      const next = [...prev];
      [next[idx], next[to]] = [next[to], next[idx]];
      return next;
    });
  };

  // Konumu olan müşteri sayısı — 2'den azsa optimize anlamsız.
  const geoCount = list.filter(hasGeo).length;

  // Mobildeki "Optimize" düğmesinin web karşılığı: noktaları en yakın-komşu
  // sezgiseliyle konuma göre otomatik sıralar (#195). Kullanıcı sonucu sürükle-bırakla
  // ince ayarlayıp "Sıralamayı Kaydet" diyebilir.
  const optimize = () => {
    const before = routeLengthKm(list);
    const optimized = optimizeByNearestNeighbor(list);
    const after = routeLengthKm(optimized);
    setList(optimized);
    const gain = Math.max(0, before - after);
    showToast?.(
      'success',
      gain > 0.05
        ? `Noktalar konuma göre sıralandı — ${after.toFixed(1)} km (${gain.toFixed(1)} km kısaldı).`
        : `Noktalar konuma göre sıralandı — ${after.toFixed(1)} km.`,
    );
  };

  const points = list
    .map((c, i) => ({
      lat: Number(pick(c, 'latitude', 'Latitude')),
      lng: Number(pick(c, 'longitude', 'Longitude')),
      order: i + 1,
      color,
      label: pick(c, 'customerTitle', 'CustomerTitle', 'title', 'Title'),
      key: pick(c, 'id', 'Id'),
    }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));

  const save = async () => {
    setBusy(true);
    try {
      const items = list.map((c, i) => ({ routePlanCustomerId: pick(c, 'id', 'Id'), visitOrderNo: i + 1 }));
      const r = await fetch(`${API_BASE}/${planId}/customers/reorder`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ items }),
      });
      const payload = await r.json().catch(() => null);
      if (!r.ok) throw new Error(pick(payload, 'Message', 'message') || 'Kaydetme başarısız');
      showToast?.('success', 'Ziyaret sırası kaydedildi.');
      onSaved?.();
      onClose?.();
    } catch (e) {
      showToast?.('error', e.message);
    } finally { setBusy(false); }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 920, width: '94%' }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <h3 style={s.modalTitle}>{readOnly ? 'Müşteri Ziyaret Sırası' : 'Müşteri Ziyaret Sırası Düzenle'}</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary,#6b7280)' }}>
              {readOnly
                ? `${dayLabel(day)} — ziyaret sırası (salt-okunur). Düzenlemek için "Düzenle"ye geçin.`
                : `${dayLabel(day)} — sürükleyerek ya da oklarla sıralamayı değiştirin.`}
            </p>
          </div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={{ ...s.modalBody, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Sıralı liste */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {!readOnly && list.length > 0 && (
              <button
                type="button"
                onClick={optimize}
                disabled={geoCount < 2}
                title={geoCount < 2
                  ? 'Optimizasyon için konumu (enlem/boylam) olan en az 2 müşteri gerekli.'
                  : 'Noktaları konumlarına göre en kısa rota olacak şekilde otomatik sırala.'}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '10px 12px', borderRadius: 10, border: 'none', fontWeight: 800, fontSize: 13,
                  color: '#fff', cursor: geoCount < 2 ? 'not-allowed' : 'pointer',
                  background: geoCount < 2 ? '#cbd5e1' : 'linear-gradient(135deg,#7a3cff,#5b8cff)',
                }}
              >
                ✨ Konuma Göre Optimize Et
              </button>
            )}
            {list.length === 0 && (
              <div style={{ color: '#9ca3af', fontSize: 13, padding: 12 }}>Bu güne atanmış müşteri yok.</div>
            )}
            {list.map((c, i) => {
              const id = pick(c, 'id', 'Id');
              return (
                <div
                  key={id}
                  draggable={!readOnly}
                  onDragStart={readOnly ? undefined : () => setDragIdx(i)}
                  onDragOver={readOnly ? undefined : (e) => e.preventDefault()}
                  onDrop={readOnly ? undefined : () => onDrop(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 12,
                    background: dragIdx === i ? 'rgba(122,60,255,0.08)' : 'var(--bg-card,#fff)',
                    cursor: readOnly ? 'default' : 'grab',
                  }}
                >
                  <span style={{
                    width: 26, height: 26, borderRadius: '50%', background: color, color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, flexShrink: 0,
                  }}>{i + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {pick(c, 'customerTitle', 'CustomerTitle', 'title', 'Title') || '—'}
                    </div>
                    <div style={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {pick(c, 'customerTextId', 'CustomerTextId') || ''} {pick(c, 'address', 'Address') ? `· ${pick(c, 'address', 'Address')}` : ''}
                    </div>
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: 'var(--accent)', background: 'rgba(0,115,234,0.1)',
                    border: '1px solid rgba(0,115,234,0.2)', padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
                  }}>{frequencyLabel(pick(c, 'frequency', 'Frequency'))}</span>
                  {!readOnly && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <button style={arrowBtn} onClick={() => move(i, -1)} disabled={i === 0} title="Yukarı">▲</button>
                      <button style={arrowBtn} onClick={() => move(i, 1)} disabled={i === list.length - 1} title="Aşağı">▼</button>
                    </div>
                  )}
                  {!readOnly && <span style={{ color: '#cbd5e1', cursor: 'grab' }} title="Sürükle">⋮⋮</span>}
                </div>
              );
            })}
          </div>

          {/* Rota önizleme */}
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary,#475569)', marginBottom: 8 }}>
              🗺 Rota önizleme — ziyaret sırasına göre
            </div>
            <RouteMap points={points} numbered showLine height={360} />
          </div>
        </div>

        <div style={{ ...s.modalFooter, justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary,#9ca3af)' }}>{list.length} nokta</span>
          <div style={{ display: 'flex', gap: 10 }}>
            {readOnly ? (
              <button style={s.btnSecondary} onClick={onClose}>Kapat</button>
            ) : (
              <>
                <button style={s.btnSecondary} onClick={onClose} disabled={busy}>Vazgeç</button>
                <button style={{ ...s.btnPrimary, opacity: busy ? 0.55 : 1 }} onClick={save} disabled={busy || list.length === 0}>
                  {busy ? 'Kaydediliyor…' : 'Sıralamayı Kaydet'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const arrowBtn = {
  border: '1px solid var(--border-color,#e5e7eb)', background: 'var(--input-bg,#fff)', borderRadius: 6,
  width: 22, height: 16, fontSize: 9, lineHeight: 1, cursor: 'pointer', color: '#64748b', padding: 0,
};
